import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { OAuthService } from '@partner/core/services/oauth.service';
import {
  DashboardDateRange,
  DashboardFilters,
  DashboardPeriod,
  DashboardScope,
  DEFAULT_DASHBOARD_FILTERS,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { DashboardClock } from '@partner/feature-modules/dashboard/infrastructure/dashboard-clock.service';
import { DashboardFiltersStorage } from '@partner/feature-modules/dashboard/infrastructure/dashboard-filters.storage';
import {
  companyToday,
  isValidCustomRange,
  resolveDateRange,
} from '@partner/feature-modules/dashboard/shared/dashboard-filters.helper';
import { DashboardFiltersStateService } from './dashboard-filters.state.service';

const TICK_INTERVAL_MS = 60_000;

function sameRange(a: DashboardDateRange | null, b: DashboardDateRange | null): boolean {
  return a?.from === b?.from && a?.to === b?.to;
}

/**
 * Public API of the dashboard filter state. Memory is authoritative; `sessionStorage` is a best-effort
 * mirror written after every state change so the selection survives a reload in the same tab.
 * A session end resets both to the defaults.
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class DashboardFiltersFacade {
  readonly filters$: Observable<DashboardFilters>;
  /** Inclusive range for the active filters, re-resolved every minute so presets roll over at company midnight; shared. */
  readonly dateRange$: Observable<DashboardDateRange | null>;
  readonly now$: Observable<Date>;

  constructor(
    private readonly state: DashboardFiltersStateService,
    private readonly storage: DashboardFiltersStorage,
    private readonly clock: DashboardClock,
    private readonly oauthService: OAuthService,
  ) {
    this.state.setFilters(this.storage.read() ?? DEFAULT_DASHBOARD_FILTERS);
    // The session end already removed the stored key.
    this.oauthService.sessionEnded$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.state.setFilters(DEFAULT_DASHBOARD_FILTERS));
    this.filters$ = this.state.getFilters$();
    // One timer and one range resolution shared by every subscriber (filter bar, pages), released when the last leaves.
    this.now$ = this.clock.tick$(TICK_INTERVAL_MS).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.dateRange$ = combineLatest([this.filters$, this.now$]).pipe(
      map(([filters, now]: [DashboardFilters, Date]) => resolveDateRange(filters, companyToday(now))),
      distinctUntilChanged(sameRange),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Keeps the custom pair so switching back to CUSTOM restores it. */
  setPeriod(period: DashboardPeriod): void {
    this.update({ ...this.state.getFilters(), period });
  }

  setScope(scope: DashboardScope): void {
    this.update({ ...this.state.getFilters(), scope });
  }

  /** `false` (state untouched) unless `from <= to <= today` in company time. */
  setCustomRange(from: IsoDate, to: IsoDate): boolean {
    if (!isValidCustomRange(from, to, companyToday(this.clock.now()))) {
      return false;
    }
    this.update({ ...this.state.getFilters(), period: DashboardPeriod.CUSTOM, customFrom: from, customTo: to });
    return true;
  }

  private update(next: DashboardFilters): void {
    this.state.setFilters(next);
    this.storage.write(next);
  }
}
